/*!
 * Sneaker Avenue — Digital Flagship
 * Homepage interaction layer. No framework, no build step — vanilla JS,
 * loaded with `defer` so it never blocks first paint (performance budget).
 * Every feature here degrades gracefully: the markup it controls is fully
 * functional/readable without JavaScript (native <form>, native <details>,
 * real hrefs on every link).
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mqMobile = window.matchMedia('(max-width: 768px)');

  (function header() {
    var hdr = document.querySelector('[data-sa-header]');
    if (!hdr) return;
    var hero = document.querySelector('.sa-hero');
    var threshold = hero ? Math.min(hero.offsetHeight - 80, window.innerHeight * 0.72) : -1;

    function onScroll() {
      hdr.classList.toggle('sa-solid', window.scrollY > threshold);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      threshold = hero ? Math.min(hero.offsetHeight - 80, window.innerHeight * 0.72) : -1;
    }, { passive: true });
  })();

  (function drawer() {
    var el = document.getElementById('sa-drawer');
    var burger = document.getElementById('sa-burger');
    if (!el || !burger) return;
    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      el.classList.add('sa-open');
      document.body.classList.add('sa-locked');
      burger.setAttribute('aria-expanded', 'true');
      var closeBtn = el.querySelector('.sa-drawer__close');
      if (closeBtn) closeBtn.focus();
    }
    function close() {
      el.classList.remove('sa-open');
      document.body.classList.remove('sa-locked');
      burger.setAttribute('aria-expanded', 'false');
      if (lastFocused) lastFocused.focus();
    }
    burger.addEventListener('click', open);
    el.querySelectorAll('[data-sa-drawer-close]').forEach(function (t) { t.addEventListener('click', close); });
    el.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', close); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el.classList.contains('sa-open')) close();
      if (e.key !== 'Tab' || !el.classList.contains('sa-open')) return;
      var focusables = el.querySelectorAll('a, button');
      if (!focusables.length) return;
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  })();

  (function search() {
    var ov = document.getElementById('sa-search-ov');
    var openBtns = document.querySelectorAll('[data-sa-search-open]');
    if (!ov || !openBtns.length) return;
    var input = ov.querySelector('#sa-search-input');
    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      ov.classList.add('sa-open');
      document.body.classList.add('sa-locked');
      if (input) setTimeout(function () { input.focus(); }, 50);
    }
    function close() {
      ov.classList.remove('sa-open');
      document.body.classList.remove('sa-locked');
      if (lastFocused) lastFocused.focus();
    }
    openBtns.forEach(function (btn) { btn.addEventListener('click', open); });
    ov.querySelectorAll('[data-sa-search-close]').forEach(function (t) { t.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && ov.classList.contains('sa-open')) close();
    });
  })();

  (function fab() {
    var fabEl = document.getElementById('sa-fab');
    var btn = document.getElementById('sa-fab-btn');
    if (!fabEl || !btn) return;
    btn.addEventListener('click', function () {
      var open = fabEl.classList.toggle('sa-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    document.addEventListener('click', function (e) {
      if (fabEl.classList.contains('sa-open') && !fabEl.contains(e.target)) {
        fabEl.classList.remove('sa-open');
        btn.setAttribute('aria-expanded', 'false');
      }
    });
  })();

  (function wishlist() {
    var KEY = 'sa_wishlist';
    function read() {
      try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
    }
    function write(ids) {
      try { localStorage.setItem(KEY, JSON.stringify(ids)); } catch (e) { }
    }
    var stored = read();

    function syncLabel(btn, pressed) {
      var label = pressed ? btn.getAttribute('data-label-remove') : btn.getAttribute('data-label-add');
      if (label) btn.setAttribute('aria-label', label);
    }

    document.querySelectorAll('[data-sa-wishlist-toggle]').forEach(function (btn) {
      var id = btn.getAttribute('data-product-id');
      var initiallyPressed = stored.indexOf(id) > -1;
      if (initiallyPressed) btn.setAttribute('aria-pressed', 'true');
      syncLabel(btn, initiallyPressed);

      btn.addEventListener('click', function () {
        var ids = read();
        var pressed = btn.getAttribute('aria-pressed') === 'true';
        var nowPressed = !pressed;
        if (nowPressed) {
          ids.push(id);
        } else {
          ids = ids.filter(function (x) { return x !== id; });
        }
        btn.setAttribute('aria-pressed', nowPressed ? 'true' : 'false');
        syncLabel(btn, nowPressed);
        write(ids);

        if (!nowPressed && document.querySelector('[data-sa-wishlist-page]')) {
          var card = btn.closest('[data-sa-product-card]');
          if (card) card.hidden = true;
          syncWishlistPageEmptyState();
        }
      });
    });

    function syncWishlistPageEmptyState() {
      var grid = document.querySelector('[data-sa-wishlist-grid]');
      var empty = document.querySelector('[data-sa-wishlist-empty]');
      if (!grid || !empty) return;
      var visible = grid.querySelectorAll('[data-sa-product-card]:not([hidden])').length;
      empty.hidden = visible > 0;
      grid.hidden = visible === 0;
    }

    var wishlistGrid = document.querySelector('[data-sa-wishlist-grid]');
    if (wishlistGrid) {
      var savedIds = read();
      wishlistGrid.querySelectorAll('[data-sa-product-card]').forEach(function (card) {
        var toggle = card.querySelector('[data-sa-wishlist-toggle]');
        var id = toggle ? toggle.getAttribute('data-product-id') : null;
        if (!id || savedIds.indexOf(id) === -1) card.hidden = true;
      });
      syncWishlistPageEmptyState();
    }
  })();

  var Cart = (function () {
    var toast = document.getElementById('sa-toast');
    var toastTimer;

    function showToast(message) {
      if (!toast) return;
      toast.textContent = message;
      toast.classList.add('sa-show');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { toast.classList.remove('sa-show'); }, 2600);
    }

    function refreshChrome(cart) {
      var count = cart.item_count;
      document.querySelectorAll('[data-sa-cartbar]').forEach(function (bar) {
        bar.hidden = count === 0;
        bar.classList.toggle('sa-show', count > 0 && window.scrollY > 200);
        var countEl = bar.querySelector('[data-sa-cartbar-count]');
        var totalEl = bar.querySelector('[data-sa-cartbar-total]');
        if (countEl) countEl.textContent = 'Your bag · ' + count + (count === 1 ? ' item' : ' items');
        if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
      });
      document.querySelectorAll('.sa-cartdot__n').forEach(function (el) { el.textContent = count; });
      var cartLinks = document.querySelectorAll('.sa-cartdot');
      cartLinks.forEach(function (a) {
        if (count > 0 && !a.querySelector('.sa-cartdot__n')) {
          var span = document.createElement('span');
          span.className = 'sa-cartdot__n';
          span.textContent = count;
          a.appendChild(span);
        }
      });
    }

    function formatMoney(cents) {
      if (window.Shopify && window.Shopify.formatMoney && window.themeMoneyFormat) {
        return window.Shopify.formatMoney(cents, window.themeMoneyFormat);
      }
      return '₱' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function add(variantId, onDone, quantity) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] })
      })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) {
              var err = new Error((data && (data.description || data.message)) || 'add failed');
              throw err;
            }
            return data;
          });
        })
        .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
        .then(function (cart) {
          refreshChrome(cart);
          showToast('Added to your bag');
          if (typeof onDone === 'function') onDone(null, cart);
        })
        .catch(function (err) {
          showToast(err && err.message ? err.message : "Couldn't add that — please try again");
          if (typeof onDone === 'function') onDone(err);
        });
    }

    function changeLine(line, quantity, onDone) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) {
              var err = new Error((data && (data.description || data.message)) || 'update failed');
              throw err;
            }
            return data;
          });
        })
        .then(function (cart) {
          refreshChrome(cart);
          if (typeof onDone === 'function') onDone(null, cart);
        })
        .catch(function (err) {
          if (typeof onDone === 'function') onDone(err);
          else showToast(err && err.message ? err.message : "Couldn't update your bag — please try again");
        });
    }

    var newArrivals = document.getElementById('new');
    function syncBarScroll() {
      var bar = document.querySelector('[data-sa-cartbar]');
      if (!bar || bar.hidden || !mqMobile.matches) return;
      var anchor = newArrivals ? newArrivals.offsetTop - 200 : 400;
      bar.classList.toggle('sa-show', window.scrollY > anchor);
    }
    window.addEventListener('scroll', syncBarScroll, { passive: true });

    return { add: add, changeLine: changeLine, showToast: showToast, formatMoney: formatMoney, refreshChrome: refreshChrome };
  })();

  (function quickAdd() {
    document.querySelectorAll('[data-sa-quick-add-open]').forEach(function (openBtn) {
      var card = openBtn.closest('[data-sa-product-card]');
      if (!card) return;
      var panel = card.querySelector('[data-sa-quick-add-panel]');
      if (!panel) return;

      openBtn.addEventListener('click', function () {
        panel.hidden = false;
        openBtn.setAttribute('aria-expanded', 'true');
        var firstSize = panel.querySelector('.sa-qap__size:not(:disabled)');
        if (firstSize) firstSize.focus();
      });
      var closeBtn = panel.querySelector('[data-sa-quick-add-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          panel.hidden = true;
          openBtn.setAttribute('aria-expanded', 'false');
        });
      }
      panel.querySelectorAll('[data-sa-add-variant]').forEach(function (sizeBtn) {
        sizeBtn.addEventListener('click', function () {
          if (sizeBtn.disabled) return;
          var status = panel.querySelector('[data-sa-quick-add-status]');
          if (status) status.textContent = 'Adding…';
          Cart.add(sizeBtn.getAttribute('data-sa-add-variant'), function (err) {
            if (err) { if (status) status.textContent = (err && err.message) || "Couldn't add — try again."; return; }
            sizeBtn.classList.add('sa-added');
            if (status) status.textContent = 'Added to your bag.';
            setTimeout(function () {
              panel.hidden = true;
              openBtn.setAttribute('aria-expanded', 'false');
              sizeBtn.classList.remove('sa-added');
              if (status) status.textContent = '';
            }, 900);
          });
        });
      });
    });
  })();

  (function productPage() {
    var section = document.querySelector('[data-sa-product-section]');
    if (!section) return;

    var stage = section.querySelector('[data-sa-pdp-stage] img');
    section.querySelectorAll('[data-sa-pdp-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        section.querySelectorAll('[data-sa-pdp-thumb]').forEach(function (t) {
          t.classList.remove('sa-on');
          t.setAttribute('aria-selected', 'false');
        });
        thumb.classList.add('sa-on');
        thumb.setAttribute('aria-selected', 'true');
        if (stage) {
          stage.src = thumb.getAttribute('data-full');
          stage.srcset = '';
          stage.alt = thumb.getAttribute('data-alt') || stage.alt;
        }
      });
    });

    var variantScript = document.querySelector('[data-sa-pdp-variants]');
    var variants = [];
    try { variants = JSON.parse(variantScript.textContent); } catch (e) { variants = []; }
    var optionGroups = section.querySelectorAll('[data-sa-option-group]');
    var variantIdInput = section.querySelector('[data-sa-pdp-variant-id]');
    var priceCurrent = section.querySelector('[data-sa-pdp-price-current]');
    var priceCompare = section.querySelector('[data-sa-pdp-price-compare]');
    var submitBtn = section.querySelector('[data-sa-pdp-submit]');
    var submitLabel = section.querySelector('[data-sa-pdp-submit-label]');
    var buyNowBtn = section.querySelector('[data-sa-pdp-buynow]');
    var statusEl = section.querySelector('[data-sa-pdp-status]');

    function selectedOptions() {
      var out = [];
      optionGroups.forEach(function (group) {
        var idx = parseInt(group.getAttribute('data-option-index'), 10);
        var active = group.querySelector('.sa-pdp__opt-btn.sa-on');
        out[idx] = active ? active.getAttribute('data-value') : null;
      });
      return out;
    }

    function findVariant(options) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var j = 0; j < options.length; j++) {
          if (options[j] && v.options[j] !== options[j]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    function applyVariant(variant) {
      if (!variant) {
        if (submitBtn) submitBtn.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Unavailable';
        if (buyNowBtn) buyNowBtn.disabled = true;
        return;
      }
      if (variantIdInput) variantIdInput.value = variant.id;
      if (priceCurrent) priceCurrent.textContent = Cart.formatMoney(variant.price);
      if (priceCompare) {
        if (variant.compare_at_price > variant.price) {
          priceCompare.textContent = Cart.formatMoney(variant.compare_at_price);
          priceCompare.hidden = false;
        } else {
          priceCompare.hidden = true;
        }
      }
      if (variant.featured_image && stage) {
        stage.src = variant.featured_image.src;
        stage.srcset = '';
      }
      if (submitBtn) submitBtn.disabled = !variant.available;
      if (buyNowBtn) buyNowBtn.disabled = !variant.available;
      if (submitLabel && submitBtn) {
        submitLabel.textContent = variant.available
          ? submitBtn.getAttribute('data-label-add')
          : submitBtn.getAttribute('data-label-soldout');
      }
    }

    optionGroups.forEach(function (group) {
      group.querySelectorAll('[data-sa-option-value]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          group.querySelectorAll('[data-sa-option-value]').forEach(function (b) {
            b.classList.remove('sa-on');
            b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('sa-on');
          btn.setAttribute('aria-pressed', 'true');
          applyVariant(findVariant(selectedOptions()));
        });
      });
    });

    var qtyInput = section.querySelector('[data-sa-pdp-qty]');
    var decrease = section.querySelector('[data-sa-qty-decrease]');
    var increase = section.querySelector('[data-sa-qty-increase]');
    function clampQty() {
      if (!qtyInput) return;
      var val = parseInt(qtyInput.value, 10);
      if (!val || val < 1) qtyInput.value = 1;
    }
    if (decrease && qtyInput) {
      decrease.addEventListener('click', function () {
        var val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = Math.max(1, val - 1);
      });
    }
    if (increase && qtyInput) {
      increase.addEventListener('click', function () {
        var val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = val + 1;
      });
    }
    if (qtyInput) qtyInput.addEventListener('change', clampQty);

    var form = document.getElementById('sa-pdp-form');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!variantIdInput || !variantIdInput.value) return;
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        if (statusEl) statusEl.textContent = 'Adding…';
        if (submitBtn) submitBtn.disabled = true;
        Cart.add(variantIdInput.value, function (err) {
          if (submitBtn) submitBtn.disabled = false;
          if (err) { if (statusEl) statusEl.textContent = (err && err.message) || "Couldn't add — please try again."; return; }
          if (statusEl) statusEl.textContent = 'Added to your bag.';
        }, qty);
      });
    }

    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', function () {
        if (buyNowBtn.disabled || !variantIdInput || !variantIdInput.value) return;
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        var buyNowLabel = section.querySelector('[data-sa-pdp-buynow-label]');
        var originalText = buyNowLabel ? buyNowLabel.textContent : '';
        buyNowBtn.disabled = true;
        if (submitBtn) submitBtn.disabled = true;
        if (buyNowLabel) buyNowLabel.textContent = 'Redirecting…';
        if (statusEl) statusEl.textContent = '';
        Cart.add(variantIdInput.value, function (err) {
          if (err) {
            buyNowBtn.disabled = false;
            if (submitBtn) submitBtn.disabled = false;
            if (buyNowLabel) buyNowLabel.textContent = originalText;
            if (statusEl) statusEl.textContent = (err && err.message) || "Couldn't start checkout — please try again.";
            return;
          }
          window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'checkout';
        }, qty);
      });
    }
  })();

  (function cartPage() {
    var cartForm = document.querySelector('[data-sa-cart-form]');
    if (!cartForm) return;

    function setLineBusy(row, busy) {
      row.classList.toggle('sa-busy', busy);
    }

    cartForm.querySelectorAll('[data-sa-cart-line]').forEach(function (row) {
      var line = parseInt(row.getAttribute('data-sa-cart-line'), 10);
      var qtyInput = row.querySelector('[data-sa-cart-qty]');
      var decrease = row.querySelector('[data-sa-cart-decrease]');
      var increase = row.querySelector('[data-sa-cart-increase]');
      var remove = row.querySelector('[data-sa-cart-remove]');

      function resyncQtyFromServer() {
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (freshCart) {
            var current = freshCart.items[line - 1];
            if (qtyInput) qtyInput.value = current ? current.quantity : 0;
          })
          .catch(function () { });
      }

      function commit(qty) {
        setLineBusy(row, true);
        Cart.changeLine(line, qty, function (err, cart) {
          setLineBusy(row, false);
          if (err) {
            resyncQtyFromServer();
            Cart.showToast(err && err.message ? err.message : "Couldn't update your bag — please try again");
            return;
          }
          if (qty === 0) {
            row.remove();
          } else {
            var lineTotal = row.querySelector('[data-sa-cart-line-total]');
            var updatedLine = cart.items[line - 1];
            if (lineTotal && updatedLine) lineTotal.textContent = Cart.formatMoney(updatedLine.final_line_price);
          }
          var subtotalEl = cartForm.querySelector('[data-sa-cart-subtotal]');
          if (subtotalEl) subtotalEl.textContent = Cart.formatMoney(cart.total_price);
          var countEl = cartForm.querySelector('[data-sa-cart-count]');
          if (countEl) countEl.textContent = cart.item_count;
          if (cart.item_count === 0) window.location.reload();
        });
      }

      if (decrease && qtyInput) {
        decrease.addEventListener('click', function () {
          var val = Math.max(0, (parseInt(qtyInput.value, 10) || 1) - 1);
          qtyInput.value = val;
          commit(val);
        });
      }
      if (increase && qtyInput) {
        increase.addEventListener('click', function () {
          var val = (parseInt(qtyInput.value, 10) || 1) + 1;
          qtyInput.value = val;
          commit(val);
        });
      }
      if (qtyInput) {
        qtyInput.addEventListener('change', function () {
          var val = Math.max(0, parseInt(qtyInput.value, 10) || 0);
          commit(val);
        });
      }
      if (remove) {
        remove.addEventListener('click', function (e) {
          e.preventDefault();
          if (qtyInput) qtyInput.value = 0;
          commit(0);
        });
      }
    });
  })();

  (function collectionFilters() {
    var form = document.querySelector('[data-sa-collection-filter-form]');
    if (!form) return;
    form.querySelectorAll('input[type="checkbox"], select[data-sa-sort-select]').forEach(function (el) {
      el.addEventListener('change', function () { form.submit(); });
    });
  })();

  (function finder() {
    var section = document.querySelector('[data-sa-finder]');
    if (!section) return;
    var card = section.querySelector('[data-sa-finder-card]');
    var indicators = section.querySelectorAll('[data-sa-finder-steps] li');
    var steps = card.querySelectorAll('[data-step]');
    var answers = {};
    var order = ['1', '2', '3', '4', '5', 'result'];
    var current = 0;
    var baseCollectionUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root + 'collections/all'
      : '/collections/all';
    var searchUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.search_url)
      ? window.Shopify.routes.search_url
      : ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root + 'search' : '/search');

    var SEARCH_TERMS = {
      running: 'running shoes',
      racing: 'racing shoes',
      pickleball: 'pickleball',
      lifestyle: 'lifestyle',
      'daily-training': 'daily trainer',
      marathon: 'marathon',
      '5k-10k': 'tempo',
      performance: 'racing shoes',
      competitive: 'racing shoes',
      hoka: 'hoka',
      asics: 'asics',
      on: 'on'
    };

    function showStep(key) {
      steps.forEach(function (s) { s.hidden = s.getAttribute('data-step') !== key; });
      var idx = order.indexOf(key);
      indicators.forEach(function (li) {
        var n = li.getAttribute('data-step-indicator');
        li.classList.toggle('sa-on', n !== null && order.indexOf(n) <= idx && key !== 'result');
      });
      current = idx;
    }

    card.querySelectorAll('[data-sa-answer]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        answers[btn.getAttribute('data-question')] = btn.getAttribute('data-tag');
        var next = order[current + 1];
        if (next) showStep(next);
        if (next === 'result') buildResultLink();
      });
    });

    card.querySelectorAll('[data-sa-finder-back]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var prev = order[current - 1];
        if (prev) showStep(prev);
      });
    });

    var restart = card.querySelector('[data-sa-finder-restart]');
    if (restart) {
      restart.addEventListener('click', function () {
        answers = {};
        showStep('1');
      });
    }

    function buildResultLink() {
      var link = card.querySelector('[data-sa-finder-result-link]');
      if (!link) return;
      var terms = [];
      Object.keys(answers).forEach(function (k) {
        var raw = answers[k];
        if (!raw || raw === 'no-preference' || raw === 'not-sure') return;
        var term = SEARCH_TERMS[raw];
        if (term && terms.indexOf(term) === -1) terms.push(term);
      });
      link.href = terms.length
        ? searchUrl + '?q=' + encodeURIComponent(terms.join(' ')) + '&type=product'
        : baseCollectionUrl;
    }

    showStep('1');
  })();

  (function reviews() {
    var track = document.querySelector('[data-sa-review-track]');
    if (!track) return;
    var prev = document.querySelector('[data-sa-review-prev]');
    var next = document.querySelector('[data-sa-review-next]');
    function step(dir) {
      var card = track.querySelector('.sa-review');
      var amount = card ? card.getBoundingClientRect().width + 24 : 300;
      track.scrollBy({ left: dir * amount, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
  })();

  (function footerAccordions() {
    var cols = document.querySelectorAll('.sa-fcol');
    if (!cols.length) return;
    function sync() {
      cols.forEach(function (d) {
        if (mqMobile.matches) d.removeAttribute('open');
        else d.setAttribute('open', '');
      });
    }
    sync();
    mqMobile.addEventListener('change', sync);
  })();

  (function scrollReveal() {
    var enabled = document.body.getAttribute('data-sa-reveal') !== 'false';
    var items = document.querySelectorAll('.sa-rv');
    if (!items.length) return;
    if (prefersReducedMotion || !enabled || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('sa-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('sa-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    items.forEach(function (el) { io.observe(el); });
  })();
})();
